'use client';

import { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import api from '@/lib/axios';
import ProductCard from '@/components/product/ProductCard';
import Button from '@/components/ui/Button';
import Slider from '@/components/ui/Slider';
import { Truck, RotateCcw, Shield, Headphones } from 'lucide-react';

interface Urun {
  id: number;
  urun_ad: string;
  marka: string;
  urun_kdv: number;
  urun_slug?: string;
  urun_resimleri: { dosya_yolu: string; alt_metin: string }[];
  urun_varyasyonlari: { id: number; varyasyon_ad: string; satis_fiyati: number; piyasa_fiyati: number; stok: number }[];
  kategori: { ad: string; slug: string };
}

interface Kategori {
  id: number;
  ad: string;
  slug: string;
  parent_id: number | null;
  sira: number;
}

// Kayan özellikler bandı - sonsuz döngü
function OzelliklerBandi({ kargoLimiti }: { kargoLimiti: string }) {
  const ozellikler = [
    { icon: Truck, baslik: 'Ücretsiz Kargo', aciklama: `${kargoLimiti}₺ üzeri siparişlerde` },
    { icon: RotateCcw, baslik: 'Kolay İade', aciklama: '30 gün içinde iade' },
    { icon: Shield, baslik: 'Güvenli Ödeme', aciklama: 'SSL korumalı ödeme' },
    { icon: Headphones, baslik: '7/24 Destek', aciklama: 'Her zaman yanınızdayız' },
  ];

  const [aktif, setAktif] = useState(0);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const ileri = () => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setAktif(p => (p + 1) % ozellikler.length);
      setAnimating(false);
    }, 400);
  };

  const baslat = () => {
    timerRef.current = setInterval(ileri, 3500);
  };

  useEffect(() => {
    baslat();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const handleDragSec = (idx: number) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setAktif(idx);
    baslat();
  };

  return (
    <div className="bg-white border-b border-gray-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        <div className="relative h-14 flex items-center justify-center">
          {ozellikler.map((o, i) => (
            <div key={o.baslik}
              className={`absolute inset-0 flex items-center justify-center gap-3 transition-all duration-400 ${
                i === aktif
                  ? 'opacity-100 translate-x-0'
                  : i === (aktif - 1 + ozellikler.length) % ozellikler.length
                  ? 'opacity-0 -translate-x-full'
                  : 'opacity-0 translate-x-full'
              }`}
              style={{ transitionDuration: '400ms' }}>
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <o.icon className="w-4 h-4 text-[var(--color-primary)]" />
              </div>
              <span className="font-semibold text-gray-800 text-sm whitespace-nowrap">{o.baslik}</span>
              <span className="text-gray-400 text-sm whitespace-nowrap">— {o.aciklama}</span>
            </div>
          ))}
          {/* Nokta göstergesi */}
          <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-1.5">
            {ozellikler.map((_, i) => (
              <button key={i} onClick={() => handleDragSec(i)}
                className={`h-1 rounded-full transition-all duration-300 ${i === aktif ? 'bg-[var(--color-primary)] w-4' : 'bg-gray-200 w-1'}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Kategoriler - sadece elle sürüklenebilir (mobil)
function KategoriKarusel({ kategoriler, renkler, emojiler }: { kategoriler: Kategori[]; renkler: string[]; emojiler: string[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const onMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (trackRef.current?.offsetLeft || 0));
    setScrollLeft(trackRef.current?.scrollLeft || 0);
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - (trackRef.current?.offsetLeft || 0);
    if (trackRef.current) trackRef.current.scrollLeft = scrollLeft - (x - startX);
  };
  const onTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].pageX - (trackRef.current?.offsetLeft || 0));
    setScrollLeft(trackRef.current?.scrollLeft || 0);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const x = e.touches[0].pageX - (trackRef.current?.offsetLeft || 0);
    if (trackRef.current) trackRef.current.scrollLeft = scrollLeft - (x - startX);
  };

  return (
    <div
      ref={trackRef}
      className="flex gap-3 overflow-x-auto pb-2 cursor-grab active:cursor-grabbing select-none"
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={() => setIsDragging(false)}
      onMouseLeave={() => setIsDragging(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
    >
      {kategoriler.map((kat, i) => (
        <Link key={kat.id} href={`/${kat.slug}`}
          className={`bg-gradient-to-br ${renkler[i % renkler.length]} text-white rounded-2xl p-4 text-center flex-shrink-0 hover:opacity-90 transition`}
          style={{ width: 140, height: 100 }}
          draggable={false}>
          <span className="text-2xl block">{emojiler[i % emojiler.length]}</span>
          <p className="font-semibold text-xs mt-2 leading-tight">{kat.ad}</p>
        </Link>
      ))}
    </div>
  );
}

export default function AnaSayfa() {
  const [urunler, setUrunler] = useState<Urun[]>([]);
  const [kategoriler, setKategoriler] = useState<Kategori[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kargoLimiti, setKargoLimiti] = useState('500');

  useEffect(() => {
    api.get('/urunler?limit=8&aktif=true').then((res) => {
      setUrunler(res.data.veri);
      setYukleniyor(false);
    });
    api.get('/kategoriler').then((res) => {
      const ana = res.data
        .filter((k: Kategori) => k.parent_id === null)
        .sort((a: Kategori, b: Kategori) => a.sira - b.sira)
        .slice(0, 6);
      setKategoriler(ana);
    });
    api.get('/site-ayarlari').then((res) => {
      if (res.data.ucretsiz_kargo_limiti) setKargoLimiti(res.data.ucretsiz_kargo_limiti);
    });
  }, []);

  const kategoriRenkler = [
    'from-blue-500 to-blue-700',
    'from-orange-400 to-orange-600',
    'from-green-500 to-green-700',
    'from-purple-500 to-purple-700',
    'from-pink-500 to-pink-700',
    'from-teal-500 to-teal-700',
  ];
  const kategoriEmojilar = ['🤸', '🧸', '🧱', '🏋️', '💊', '🛋️'];

  return (
    <div>
      <Slider />

      <OzelliklerBandi kargoLimiti={kargoLimiti} />

      {/* Kategoriler */}
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-[var(--color-gray-800)]">Kategoriler</h2>
          <Link href="/urunler" className="text-[var(--color-primary)] text-sm font-medium hover:underline">Tümünü Gör →</Link>
        </div>

        {/* Masaüstü: grid */}
        <div className="hidden md:grid grid-cols-3 lg:grid-cols-6 gap-4">
          {kategoriler.map((kat, i) => (
            <Link key={kat.id} href={`/${kat.slug}`}
              className={`bg-gradient-to-br ${kategoriRenkler[i % kategoriRenkler.length]} text-white rounded-[var(--radius-xl)] p-5 text-center hover:opacity-90 transition-opacity shadow-[var(--shadow-md)]`}>
              <span className="text-3xl block mb-2">{kategoriEmojilar[i % kategoriEmojilar.length]}</span>
              <p className="font-semibold text-sm">{kat.ad}</p>
            </Link>
          ))}
        </div>

        {/* Mobil: elle sürüklenebilir */}
        <div className="md:hidden">
          <KategoriKarusel kategoriler={kategoriler} renkler={kategoriRenkler} emojiler={kategoriEmojilar} />
        </div>
      </div>

      {/* Öne Çıkan Ürünler */}
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-[var(--color-gray-800)]">Öne Çıkan Ürünler</h2>
          <Link href="/urunler" className="text-[var(--color-primary)] text-sm font-medium hover:underline">Tümünü Gör →</Link>
        </div>
        {yukleniyor ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-[var(--color-gray-200)] animate-pulse rounded-[var(--radius-xl)] h-72" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {urunler.map((urun) => (
              <ProductCard
                key={urun.id}
                id={urun.id}
                urun_ad={urun.urun_ad}
                marka={urun.marka}
                urun_kdv={urun.urun_kdv}
                resim={urun.urun_resimleri?.[0]?.dosya_yolu}
                resim_alt={urun.urun_resimleri?.[0]?.alt_metin}
                resimler={urun.urun_resimleri}
                varyasyonlar={urun.urun_varyasyonlari}
                kategori={urun.kategori?.ad}
                href={urun.urun_slug ? `/${urun.urun_slug}` : `/urunler/${urun.id}`}
              />
            ))}
          </div>
        )}
        <div className="text-center mt-10">
          <Link href="/urunler">
            <Button variant="outline" size="lg">Tüm Ürünleri Gör</Button>
          </Link>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-[var(--color-secondary)] to-orange-400 mx-4 md:mx-8 rounded-[var(--radius-2xl)] p-8 md:p-12 my-12 text-white text-center shadow-[var(--shadow-xl)]">
        <h2 className="text-2xl md:text-4xl font-bold mb-4">🏆 Profesyonel Cimnastikçiler İçin</h2>
        <p className="text-lg mb-6 text-orange-100">Dünya standartlarında ekipmanlar, uzman desteği ile</p>
        <Link href="/kategoriler/cimnastik"
          className="inline-block px-8 py-3 border-2 border-white text-white font-semibold rounded-[var(--radius-lg)] hover:bg-white hover:text-orange-500 transition-all">
          Profesyonel Ürünleri Keşfet
        </Link>
      </div>
    </div>
  );
}
